/// <amd-module name="Artifacts/Package/services/FollowsDataService" />
import { FeedServiceInstanceId } from "Artifacts/Root/Constants";
import { IFeed, IPackage } from "Artifacts/Root/types";
import {
  ArtifactFilter,
  NotificationSubscription,
  SubscriptionQuery,
  SubscriptionQueryCondition,
  SubscriptionQueryFlags,
} from "Notifications/Common/Contracts/Notification";
import { INotificationRestClient } from "Notifications/Common/RestClient/Notification";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IPageData, IVssPageService } from "VSS/Platform/Page";

export interface IFeedFollowsDataService extends IVssService {
  getFollowState(packageSummary: IPackage, feed: IFeed): Promise<NotificationSubscription>;
  follow(packageSummary: IPackage, feed: IFeed): Promise<NotificationSubscription>;
  unfollow(packageSummary: IPackage, feed: IFeed): Promise<void>;
}

class FollowsDataService extends VssService {
  private static readonly ArtifactType: string = "Package";
  private static readonly FollowsType = "Artifact";

  public async getFollowState(packageSummary: IPackage, feed: IFeed): Promise<boolean> {
    if (!packageSummary || !feed) {
      throw new Error("Invalid arguments");
    }

    const artifactId: string = this.getArtifactId(feed, packageSummary);
    const subQuery: SubscriptionQuery | null = this.getSubscriptionQuery(
      FollowsDataService.ArtifactType,
      artifactId,
    );

    const subscriptions = await this.NotificationRestClient.querySubscriptions(subQuery);
    return subscriptions != null && subscriptions.length > 0;
  }

  public async follow(packageSummary: IPackage, feed: IFeed): Promise<NotificationSubscription> {
    if (!packageSummary || !feed) {
      throw new Error("Invalid arguments");
    }

    // Doing a check of current subscriptions so that we don't create duplicates.
    const artifactId: string = this.getArtifactId(feed, packageSummary);
    const subQuery: SubscriptionQuery | null = this.getSubscriptionQuery(
      FollowsDataService.ArtifactType,
      artifactId,
    );
    const subscriptions = await this.NotificationRestClient.querySubscriptions(subQuery);

    const subscriptionObj: NotificationSubscription = this.createSubscriptionObject(
      FollowsDataService.ArtifactType,
      artifactId,
    );

    if (subscriptions != null && subscriptions.length > 0) {
      return subscriptions[0];
    }
    const returned = await this.NotificationRestClient.createSubscription(subscriptionObj);

    return returned;
  }

  public async unfollow(packageSummary: IPackage, feed: IFeed): Promise<void> {
    if (!packageSummary || !feed) {
      throw new Error("Invalid arguments");
    }

    const artifactId: string = this.getArtifactId(feed, packageSummary);
    const subQuery: SubscriptionQuery | null = this.getSubscriptionQuery(
      FollowsDataService.ArtifactType,
      artifactId,
    );

    const subscriptions = await this.NotificationRestClient.querySubscriptions(subQuery);

    if (subscriptions != null && subscriptions.length > 0) {
      const subscriptionToUnfollow = subscriptions[0];
      const subscriptionId = subscriptionToUnfollow.id;
      if (subscriptionId == null || subscriptionId === "") {
        throw new Error("No subscriptions were found to unfollow");
      }

      return this.NotificationRestClient.deleteSubscription(subscriptionId);
    }

    throw new Error("No subscriptions were found to unfollow");
  }

  private getArtifactId(feed: IFeed, packageSummary: IPackage): string {
    let artifactId: string;
    if (feed.selectedViewId) {
      artifactId = feed.id + "@" + feed.selectedViewId + "/" + packageSummary.id;
    } else {
      artifactId = feed.id + "/" + packageSummary.id;
    }
    return artifactId;
  }

  private get NotificationRestClient() {
    return this.pageContext.getRestClient<INotificationRestClient>("INotificationRestClient", {
      serviceInstanceType: FeedServiceInstanceId,
    });
  }

  // getSubscriptionQuery() and createSubscriptionObject() are copied from
  // \Notifications\Web\extensions\vss-notifications-web\notifications-common\Services\FollowsService.ts

  /**
   * Gets a subscription query filled in with the specified parameters
   * @param artifactType Type of artifact to query for
   * @param artifactId Specific artifact id, if any.  If not specified will return all subscriptions for the
   *                      specified artifact type.
   */
  private getSubscriptionQuery(
    artifactType: string,
    artifactId?: string,
  ): SubscriptionQuery | null {
    // get the current user id.
    const userId: string | null = this.getUserId();

    if (!userId) {
      return null;
    }

    const wif = {
      artifactId,
      artifactType,
      type: FollowsDataService.FollowsType,
    } as ArtifactFilter;

    const wic = ({
      subscriptionId: null,
      filter: wif,
      scope: null,
      subscriberId: userId,
      flags: null,
    } as unknown) as SubscriptionQueryCondition;

    return { conditions: [wic], queryFlags: SubscriptionQueryFlags.None };
  }

  /**
   * Creates a new subscription object to follow a specific artifact
   */
  private createSubscriptionObject(
    artifactType: string,
    artifactId: string,
  ): NotificationSubscription {
    const filter = {
      artifactId,
      artifactType,
      type: FollowsDataService.FollowsType,
    } as ArtifactFilter;

    const notificationSubscription = ({
      diagnostics: null,
      _links: null,
      adminSettings: null,
      channel: null,
      flags: null,
      lastModifiedBy: null,
      modifiedDate: null,
      scope: null,
      status: null,
      statusMessage: "",
      // tslint:disable-next-line:no-any
      subscriber: <any>this.getUserId(),
      url: "",
      userSettings: null,
      description: "",
      extendedProperties: null,
      id: undefined,
      filter,
      permissions: null,
    } as unknown) as NotificationSubscription;

    return <NotificationSubscription>notificationSubscription;
  }

  /**
   * Gets the current user's id from the page context, or null if it cannot get it.
   */
  private getUserId(): string | null {
    const pageService: IVssPageService = this.pageContext.getService<IVssPageService>(
      "IVssPageService",
    );
    const pageData: IPageData | undefined = pageService.getData();

    if (pageData) {
      return pageData.user.id;
    } else {
      // something is broken if we don't have a user id.
      return null;
    }
  }
}

Services.add("IFeedFollowsDataService", { serviceFactory: FollowsDataService });
